import React, { CSSProperties, ChangeEvent, FC, useCallback, useState } from 'react';
import { MdClose } from 'react-icons/md';

import { styled } from '../theme';
import TruncatedTextWithTooltip, {
    TruncatedTextWithTooltipContentProps,
} from './TruncatedTextWithTooltip';
import { FlexRow, Input } from './index';

const InputContainer = styled('div')(({ theme }) => ({
    position: 'relative',
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.roman,
    fontSize: '1.6rem',
    outline: 'none',
    lineHeight: 20,
}));

const InputFieldContainer = styled(Input)<{ status?: string }>(({ theme, status }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    outline: 'none',
    width: '100%',
    padding: '5px 40px 5px 15px',
    cursor: 'text',
    color: theme.color.secondaryText,
    backgroundColor: theme.color.secondary1,
    height: 'auto',
    ...(status && {
        border: `1px solid ${theme.color[status]}`,
    }),
}));

const InputField = styled('input')(({ theme }) => ({
    background: 'transparent',
    border: 'none',
    height: 40,
    padding: 0,
    font: 'inherit',
    cursor: 'inherit',
    lineHeight: 20,
    width: 340,
    caretColor: theme.color.primary1,
    '::placeholder': {
        color: theme.color.gray40,
    },
}));

const SelectedOptionContainer = styled(FlexRow)(({ theme }) => ({
    margin: '5px 10px 5px 0',
    padding: '5px 8px',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    borderRadius: theme.borderRadius.base,
    minWidth: 0,
    maxWidth: '100%',
}));

const SelectedOption = styled('div')({
    width: '100%',
});

const SelectedOptionRemoveButton = styled('div')({
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    padding: 0,
    marginLeft: 4,
    height: 20,
});

const StyledClearIcon = styled(MdClose)(({ theme }) => ({
    verticalAlign: 'top',
    height: 20,
    marginRight: -2,
    color: theme.color.primary1,
    ':hover, :focus': {
        color: theme.color.red,
    },
    ':active': {
        color: theme.color.primary3,
    },
}));

const ClearAllWrapper = styled('div')({
    height: '100%',
    position: 'absolute',
    padding: 0,
    top: 0,
    right: 14,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
});

const SelectedTag: FC<{
    tag: string;
    onRemove: (tag: string) => void;
    tooltipStyle?: CSSProperties;
}> = ({ tag, onRemove, tooltipStyle }) => {
    const renderContent = useCallback(
        (props: TruncatedTextWithTooltipContentProps) => (
            <SelectedOption {...props}>{tag}</SelectedOption>
        ),
        [tag]
    );

    const handleRemove = useCallback(() => onRemove(tag), [onRemove, tag]);

    return (
        <SelectedOptionContainer>
            <TruncatedTextWithTooltip
                text={tag}
                renderContent={renderContent}
                tooltipStyle={tooltipStyle}
            />
            <SelectedOptionRemoveButton onClick={handleRemove}>
                <StyledClearIcon />
            </SelectedOptionRemoveButton>
        </SelectedOptionContainer>
    );
};

type Props = {
    value: string[];
    name: string;
    onChange: (e: { target: { name: string; value: string[] } }) => void;
    placeholder?: string;
    tooltipStyle?: CSSProperties;
};

const TagInput: React.FC<Props> = ({ onChange, value, name, placeholder, tooltipStyle }) => {
    const [inputValue, setInputValue] = useState<string>('');

    const changeValue = (newValue: string[]) => {
        onChange({ target: { name, value: newValue } });
    };

    const addTag = (tag: string) => {
        const tagToAdd = tag.trim();
        if (tagToAdd && !value.includes(tagToAdd)) {
            changeValue([...value, tagToAdd]);
        }
        setInputValue('');
    };

    const removeTag = (tagToRemove: string) => {
        changeValue(value.filter((tag) => tag !== tagToRemove));
    };

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (['Enter', 'Tab', ' '].includes(e.key)) {
                e.preventDefault();
                addTag(inputValue);
            }
        },
        [inputValue]
    );

    const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    }, []);

    const handleClear = useCallback(() => {
        changeValue([]);
    }, []);

    return (
        <InputContainer>
            <InputFieldContainer as="div">
                <InputField
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                />
                {value.map((tag) => (
                    <SelectedTag
                        key={tag}
                        tag={tag}
                        onRemove={removeTag}
                        tooltipStyle={tooltipStyle}
                    />
                ))}
            </InputFieldContainer>
            <ClearAllWrapper onClick={handleClear} aria-label="clear selection">
                <StyledClearIcon />
            </ClearAllWrapper>
        </InputContainer>
    );
};

export default TagInput;
